import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosAdapter,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { CapacitorHttp, type HttpOptions, type HttpResponse } from '@capacitor/core';

/**
 * Запросы приложения через сетевой слой ОС — то, чего не умеет подмена XHR в Capacitor (02.10.2026, сборка 1.1.1).
 *
 * ⚠️ ЗАЧЕМ. Закрепление сертификата (`NSPinnedDomains`) на XHR из WKWebView не действует — проверено Proxyman
 * 27.09.2026 (техдолг 314). Оно применяется только к `URLSession`, то есть к CapacitorHttp. Включённый CapacitorHttp
 * подменяет `window.fetch` и `XMLHttpRequest` ГЛОБАЛЬНО (`@capacitor/ios` → `native-bridge.js`): GET уходит прежним
 * XHR через внутренний адрес, а POST/PUT/PATCH/DELETE — вызовом плагина. И вот что плагин при этом теряет:
 *
 * 1. **User-Agent.** Уходит системный (CFNetwork) — метка сборки `1BilimAcademy/… (N; ios)` из `appendUserAgent` в
 *    плагин не попадает. Сервер по ней отличает приложение от сайта: без неё код входа ушёл бы письмом сайта
 *    (`shared/middleware/client.go`). Заголовок `User-Agent` из JS плагин берёт — ставим его сами.
 * 2. **Таймаут.** Мост его не передаёт: на iOS запрос ждёт 600 с, `ontimeout` не срабатывает никогда. Продление
 *    сессии держит за собой всю очередь 401, очередь ответов (`lib/outbox.ts`) — проход целиком. Таймер свой.
 * 3. **Двоичный ответ.** `responseType: 'blob'` мост не передаёт, и mp3 на iOS превращается в строку utf-8 —
 *    то есть в ничто. Голос экзаменатора (`speakingApi.synthesize`) молчал бы. Такой запрос идёт плагину напрямую.
 * 4. **Процент отправки.** События `upload.progress` нет вовсе — см. `uploadProgressWorks`.
 *
 * ⚠️ САЙТ И ВЫКЛЮЧЕННЫЙ ФЛАГ — БЕЗ ЕДИНОГО ИЗМЕНЕНИЯ. Подмены нет (`httpPatched() === false`) — запрос уходит
 * штатному адаптеру axios тем же объектом настроек. Флаг живёт в `capacitor.config.ts` и равен `pinning.json`:
 * pin без CapacitorHttp ничего не закрепляет, поэтому включаются только вместе. Сторож — `nativeHttp.test.ts`.
 *
 * ⚠️ ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО. Обхода подмены через сохранённый мостом настоящий XHR для «неудобных» запросов:
 * он вывел бы их из-под pin. И отмены на стороне ОС: `abort()` у подменённого XHR гасит только JS-сторону,
 * сам запрос доходит — после таймаута повтор очереди может прийти на сервер дважды. Ответ и сдача это переживают
 * (одна строка на ученика и задание); повторная загрузка файла оставляет в хранилище лишнюю копию.
 */

type PatchedWindow = Window & { CapacitorWebFetch?: typeof fetch };

/**
 * Подменил ли мост `fetch` и XHR. Оригинал он сохраняет ВСЕГДА (`native-bridge.js`: `win.CapacitorWebFetch =
 * window.fetch`), а подменяет только при включённом флаге — отсюда сравнение. На сайте моста нет: `false`.
 */
export function httpPatched(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as PatchedWindow;
  return typeof w.CapacitorWebFetch === 'function' && window.fetch !== w.CapacitorWebFetch;
}

/**
 * Есть ли у загрузки честный процент. Под CapacitorHttp его нет: файл уходит одним вызовом плагина, и полоса
 * стояла бы на «0 %» до самого конца. Тогда показываем «Отправляем…» без числа (`api/uploads.ts`).
 */
export function uploadProgressWorks(): boolean {
  return !httpPatched();
}

/** Методы, которые подмена оставляет настоящему XHR (через внутренний адрес): им наша помощь не нужна. */
const STAYS_XHR: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

function methodOf(config: InternalAxiosRequestConfig): string {
  return (config.method ?? 'get').toUpperCase();
}

/** Пойдёт ли запрос вызовом плагина: не-GET и абсолютный адрес (относительные мост не трогает). */
function goesThroughPlugin(config: InternalAxiosRequestConfig): boolean {
  if (STAYS_XHR.has(methodOf(config))) return false;
  return /^https?:\/\//i.test(axios.getUri(config));
}

export type NativeHttpDeps = {
  /** Штатный адаптер axios (XHR). */
  base: AxiosAdapter;
  patched: () => boolean;
  userAgent: () => string;
  native: (options: HttpOptions) => Promise<HttpResponse>;
};

function headerOf(headers: Record<string, unknown> | undefined, name: string): string {
  if (!headers) return '';
  const key = Object.keys(headers).find((k) => k.toLowerCase() === name);
  return key ? String(headers[key] ?? '') : '';
}

function plainHeaders(headers: AxiosHeaders): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers.toJSON() as Record<string, unknown>)) {
    if (v === null || v === undefined || v === false) continue;
    out[k] = String(v);
  }
  return out;
}

/** Тело ответа плагина → байты. Двоичное приходит base64, JSON (отказ 4xx) — уже разобранным. */
function toBytes(data: unknown, contentType: string): Uint8Array {
  if (typeof data !== 'string' || contentType.toLowerCase().includes('application/json')) {
    return new TextEncoder().encode(JSON.stringify(data ?? null));
  }
  const bin = atob(data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * Запрос с двоичным ответом — плагину напрямую, с `responseType`: только так он отдаёт base64, а не строку.
 * Тело — только строка (JSON после `transformRequest`) или пусто: другого потребителя нет, а молча отправить
 * `FormData` как «[object FormData]» хуже явного отказа.
 */
async function requestBinary(config: InternalAxiosRequestConfig, deps: NativeHttpDeps): Promise<AxiosResponse> {
  if (config.data != null && typeof config.data !== 'string') {
    throw new AxiosError('nativeHttp: binary response is supported for a JSON body only', AxiosError.ERR_BAD_REQUEST, config);
  }
  const ms = config.timeout && config.timeout > 0 ? config.timeout : undefined;
  let native: HttpResponse;
  try {
    native = await deps.native({
      url: axios.getUri(config),
      method: methodOf(config),
      headers: plainHeaders(AxiosHeaders.from(config.headers)),
      data: config.data ?? undefined,
      responseType: config.responseType as HttpOptions['responseType'],
      connectTimeout: ms,
      readTimeout: ms,
    });
  } catch (e) {
    // Плагин отказал, не получив ответа: обрыв, таймаут ОС, сорванное рукопожатие (чужой сертификат).
    throw new AxiosError((e as Error | undefined)?.message || 'Network Error', AxiosError.ERR_NETWORK, config);
  }
  const contentType = headerOf(native.headers, 'content-type');
  const bytes = toBytes(native.data, contentType);
  const data =
    config.responseType === 'arraybuffer'
      ? bytes.buffer
      : new Blob([bytes as BlobPart], { type: contentType || 'application/octet-stream' });
  const response: AxiosResponse = {
    data,
    status: native.status,
    statusText: '',
    headers: native.headers ?? {},
    config,
    request: null,
  };
  const valid = config.validateStatus ? config.validateStatus(native.status) : native.status >= 200 && native.status < 300;
  if (valid) return response;
  throw new AxiosError(
    `Request failed with status code ${native.status}`,
    native.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
    config,
    null,
    response,
  );
}

export function createNativeSafeAdapter(deps: NativeHttpDeps): AxiosAdapter {
  return async function nativeSafeAdapter(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
    // Сайт, выключенный флаг, GET — штатный путь, тем же объектом настроек.
    if (!deps.patched() || !goesThroughPlugin(config)) return deps.base(config);

    // 1. Метка сборки: без неё сервер принял бы приложение за сайт.
    // Копия, а не `AxiosHeaders.from`: тот вернул бы сам объект запроса, и метка осталась бы в исходных настройках.
    const headers = new AxiosHeaders(config.headers);
    headers.set('User-Agent', deps.userAgent());

    // Отмена: свой сигнал, чужой (если запрос отменяют снаружи) пробрасываем в него.
    const controller = new AbortController();
    const outer = config.signal as AbortSignal | undefined;
    if (outer?.aborted) controller.abort();
    else outer?.addEventListener?.('abort', () => controller.abort(), { once: true });

    const own: InternalAxiosRequestConfig = { ...config, headers, signal: controller.signal };

    // 3. Двоичный ответ — плагину напрямую.
    const binary = config.responseType === 'blob' || config.responseType === 'arraybuffer';
    const run = binary ? requestBinary(own, deps) : Promise.resolve(deps.base(own));

    // 2. Таймаут: тот же код и текст, что у axios на сайте, — `isNetworkError` и очередь видят одно и то же.
    const ms = config.timeout && config.timeout > 0 ? config.timeout : 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const raced = ms
      ? Promise.race([
          run,
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => {
              reject(new AxiosError(config.timeoutErrorMessage || `timeout of ${ms}ms exceeded`, AxiosError.ECONNABORTED, config));
              controller.abort();
            }, ms);
          }),
        ])
      : run;
    // После таймаута `run` ещё упадёт отменой — её уже никто не ждёт.
    run.catch(() => undefined);

    try {
      const response = await raced;
      // Наружу — исходные настройки: повтор после 401 зовёт клиент с `error.config`/`response.config`.
      response.config = config;
      return response;
    } catch (e) {
      if (axios.isAxiosError(e)) e.config = config;
      throw e;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };
}

/**
 * Адаптер клиента (`api/client.ts`): у экземпляра и у продления сессии. Штатный адаптер берётся в момент
 * запроса, не запоминается: проверки подменяют `axios.defaults.adapter`, и запомненный пережил бы подмену.
 */
export const nativeSafeAdapter: AxiosAdapter = createNativeSafeAdapter({
  base: (config) => axios.getAdapter(axios.defaults.adapter)(config),
  patched: httpPatched,
  userAgent: () => navigator.userAgent,
  native: (options) => CapacitorHttp.request(options),
});
